/**
 * Team → Reviews.
 *
 * The review inbox: markdown documents the AI employees filed for the
 * operator (`work/for_review/todo/` in their repos, mirrored to the server
 * at drop-off). Rows arrive in the server's triage order — back from snooze,
 * then the main queue (priority ascending, oldest filing on top: the todo/
 * folder's own order), then snoozed rows, then history — and every row opens
 * the full-page viewer where comments are anchored to source lines.
 *
 * Documents group by their topic (`lib/reviewGroups.ts`): a group sits where
 * its first document stands in that order, and folds on a click. In the
 * Pending view "Back from snooze" sits on top, then the topic groups of the
 * main queue, then a collapsed "Snoozed" group.
 * Above them sits the folded "Recently worked on" group – copies of the
 * documents the owner acted on, newest action first, finished ones behind
 * "Show finished" – like on the email inbox.
 * A pending row approves inline (button or `a`), like an email draft.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";

import { adminApi, type AdminReviewDoc, type ReviewStatus } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { Age } from "../../components/Age";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { PriorityBadge } from "../../components/PriorityBadge";
import { QueryBoundary } from "../../components/QueryBoundary";
import { ReviewGroup } from "../../components/review/ReviewGroup";
import { SnoozeControl } from "../../components/review/SnoozeMenu";
import { SnoozedSection } from "../../components/review/SnoozedSection";
import { RecentlyWorkedSection } from "../../components/review/RecentlyWorkedSection";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { FilterChips } from "../../components/ui/FilterChips";
import { ShortcutHelp, ShortcutHint } from "../../components/ui/ShortcutHelp";
import { reviewStatusTone, reviewUrl } from "../../lib/aiTeam";
import { ageSeconds } from "../../lib/console";
import { useListNavigation, type ListRowProps } from "../../lib/hooks/useListNavigation";
import { useUrlFilter } from "../../lib/hooks/useUrlFilter";
import { finishOutcome, finishQuestion, type FinishContext } from "../../lib/reviewFinish";
import { groupByTopic, toggled } from "../../lib/reviewGroups";
import { reviewQueue, splitByQueue } from "../../lib/reviewQueue";
import { submissionTitle } from "../../lib/reviewVersions";
import { formatReturnDate, snoozedUntilDate } from "../../lib/snooze";

type ReviewFilter = ReviewStatus | "all";

const REVIEW_FILTERS: { value: ReviewFilter; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "changes_requested", label: "Changes requested" },
  { value: "approved", label: "Approved" },
  { value: "done", label: "Done" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "all", label: "All" },
];
const FILTER_VALUES = REVIEW_FILTERS.map((f) => f.value);

export function ReviewsPage() {
  const [filter, setFilter] = useUrlFilter<ReviewFilter>("status", FILTER_VALUES, "pending");
  // kind=doc — email drafts have their own tab (/team/email-drafts).
  const reviewsQuery = adminApi.aiTeam.reviews(filter, "doc");
  const recentQuery = adminApi.aiTeam.recentReviews("doc");
  const snooze = adminApi.aiTeam.snoozeReview();
  const wake = adminApi.aiTeam.wakeReview();
  const finish = adminApi.aiTeam.finishReview();
  const setRecent = adminApi.aiTeam.setReviewRecent();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [nowMs] = useState(() => Date.now());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const rows = reviewsQuery.data ?? [];
  const queues = filter === "pending" ? splitByQueue(rows) : null;
  const groups = groupByTopic(queues ? queues.main : rows);
  // Keyboard order = visual order: returned rows, then unfolded groups.
  const actionable = [
    ...(queues?.returned ?? []),
    ...groups.filter((g) => !collapsed.has(g.id)).flatMap((g) => g.docs),
  ];

  // The × takes a document out of "Recently worked on"; Undo puts it back
  // at its earlier place.
  const removeRecent = (doc: AdminReviewDoc) => {
    const workedAt = doc.owner_worked_at ?? null;
    setRecent.mutate({ reviewId: doc.id, workedAt: null }, {
      onSuccess: () => toast.ok(`"${doc.title}" removed from Recently worked on`, {
        action: workedAt
          ? { label: "Undo", onClick: () => setRecent.mutate({ reviewId: doc.id, workedAt }, { onError: (e) => toast.error(e) }) }
          : undefined,
      }),
      onError: (e) => toast.error(e),
    });
  };

  const doSnooze = (doc: AdminReviewDoc, days: number) =>
    snooze.mutate(
      { reviewId: doc.id, days },
      {
        onSuccess: (res) =>
          toast.ok(
            res.snoozed_until
              ? `Snoozed until ${formatReturnDate(snoozedUntilDate(res.snoozed_until), Date.now())}`
              : "Snoozed",
          ),
        onError: (e) => toast.error(e),
      },
    );

  const approve = async (doc: AdminReviewDoc) => {
    const ctx: FinishContext = { author: doc.agent_slug, isEmail: false, feedbackCount: 0, subject: doc.title };
    if (!(await confirm(finishQuestion("approve", undefined, ctx)))) return;
    finish.mutate({ reviewId: doc.id, decision: "approve" }, {
      // Comments drafted on the detail page go along as notes; the toast
      // counts what the finish actually sent.
      onSuccess: (r) => toast.ok(finishOutcome(r, { ...ctx, feedbackCount: r.comments?.length ?? 0 }).text),
      onError: (e) => toast.error(e),
    });
  };

  const nav = useListNavigation({
    items: actionable,
    getKey: (d) => d.id,
    onOpen: (d) => navigate(reviewUrl(d.id)),
    onApprove: (d) => void approve(d),
    canApprove: (d) => d.status === "pending" && !finish.isPending,
    onSnooze: (d) => setMenuFor(d.id),
    canSnooze: (d) => d.status === "pending",
    enabled: menuFor === null,
  });

  const renderRow = (doc: AdminReviewDoc, showTopic = false) => (
    <ReviewRow
      key={doc.id}
      doc={doc}
      showTopic={showTopic}
      nowMs={nowMs}
      active={nav.isActive(doc)}
      linkProps={nav.rowProps(doc)}
      menuOpen={menuFor === doc.id}
      onMenuOpenChange={(open) => setMenuFor(open ? doc.id : null)}
      onSnooze={(days) => doSnooze(doc, days)}
      snoozing={snooze.isPending && snooze.variables?.reviewId === doc.id}
      approving={finish.isPending}
      onApprove={() => void approve(doc)}
    />
  );

  const renderGroups = () =>
    groups.map((group) => {
      const waiting = group.docs.filter((d) => d.status === "pending");
      return (
        <ReviewGroup
          key={group.id}
          name={group.name}
          count={group.docs.length}
          urgent={waiting.filter((d) => d.priority === 1).length}
          oldestWaitingSeconds={waiting.length ? Math.max(0, ...waiting.map((d) => ageSeconds(d.submitted_at, nowMs))) : null}
          folded={collapsed.has(group.id)}
          onToggle={() => setCollapsed((current) => toggled(current, group.id))}
        >
          {group.docs.map((d) => renderRow(d))}
        </ReviewGroup>
      );
    });

  return (
    <>
      <PageHeader
        title="AI Reviews"
        description="Documents the AI employees filed for your review — comment on lines, finish, and the feedback lands back in their queue."
      />

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <FilterChips
          aria-label="Review status"
          options={REVIEW_FILTERS}
          value={filter}
          onChange={setFilter}
        />
        <div className="text-sm text-ink-low">
          {queues ? (
            <>
              <span className="tnum font-medium text-ink">{queues.main.length}</span> to review
              {queues.returned.length ? ` · ${queues.returned.length} back` : ""}
              {queues.snoozed.length ? ` · ${queues.snoozed.length} snoozed` : ""}
            </>
          ) : (
            <>
              <span className="tnum font-medium text-ink">{rows.length}</span> document
              {rows.length === 1 ? "" : "s"}
            </>
          )}
        </div>
        <span className="ml-auto">
          <ShortcutHint />
        </span>
      </div>

      {queues ? (
        <RecentlyWorkedSection
          className="mb-4"
          rows={recentQuery.data ?? []}
          linkTo={(d) => reviewUrl(d.id)}
          title={(d) => d.title}
          detail={(d) => d.path}
          onRemove={removeRecent}
          removingId={setRecent.isPending ? setRecent.variables?.reviewId : null}
        />
      ) : null}

      <QueryBoundary
        query={reviewsQuery}
        empty={
          filter === "pending" ? (
            <div className="empty-state">
              <div className="title">Nothing to review</div>
              <p className="hint">
                No documents are waiting on you — finished ones are one filter
                away.
              </p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="title">Nothing here</div>
              <p className="hint">No documents match this filter.</p>
            </div>
          )
        }
        isEmpty={(list) => list.length === 0}
      >
        {() =>
          queues ? (
            <div className="space-y-4">
              {queues.returned.length ? (
                <section className="panel panel--flush">
                  <h2 className="queue-group-title">
                    Back from snooze <span className="font-mono text-xs text-ink-low">{queues.returned.length}</span>
                  </h2>
                  <ul className="divide-y divide-rule border-t border-rule">{queues.returned.map((d) => renderRow(d, true))}</ul>
                </section>
              ) : null}
              {queues.main.length ? (
                renderGroups()
              ) : queues.returned.length === 0 ? (
                <div className="empty-state">
                  <div className="title">Nothing to review</div>
                  <p className="hint">Everything pending is snoozed – it comes back on its own.</p>
                </div>
              ) : null}
              <SnoozedSection
                rows={queues.snoozed}
                linkTo={(d) => reviewUrl(d.id)}
                title={(d) => d.title}
                wakingId={wake.isPending ? wake.variables?.reviewId : null}
                onWake={(d) =>
                  wake.mutate(
                    { reviewId: d.id },
                    {
                      onSuccess: () => toast.ok(`"${d.title}" is back`),
                      onError: (e) => toast.error(e),
                    },
                  )
                }
              />
            </div>
          ) : (
            <div className="space-y-4">{renderGroups()}</div>
          )
        }
      </QueryBoundary>

      <ShortcutHelp open={nav.helpOpen} onClose={() => nav.setHelpOpen(false)} shortcuts={nav.shortcuts} />
    </>
  );
}

function ReviewRow({
  doc,
  showTopic,
  nowMs,
  active,
  linkProps,
  menuOpen,
  onMenuOpenChange,
  onSnooze,
  snoozing,
  approving,
  onApprove,
}: {
  doc: AdminReviewDoc;
  /** Outside a topic group ("Back from snooze") the row names its topic. */
  showTopic: boolean;
  nowMs: number;
  active: boolean;
  linkProps: ListRowProps;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onSnooze: (days: number) => void;
  snoozing: boolean;
  approving: boolean;
  onApprove: () => void;
}) {
  const queue = reviewQueue(doc);
  return (
    <li className={clsx("flex items-center gap-3 px-4 hover:bg-surface-tint", active && "row-cursor")}>
      <Link className="flex min-w-0 flex-1 items-center gap-3 py-2.5" to={reviewUrl(doc.id)} {...linkProps}>
        <PriorityBadge priority={doc.priority} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-ink truncate">{doc.title}</span>
          <span className="block font-mono text-xs text-ink-low truncate">{doc.path}</span>
        </span>
        {showTopic ? (
          <span className="text-xs text-ink-low shrink-0">{doc.topic?.name ?? "Uncategorized"}</span>
        ) : null}
        {doc.agent_slug ? (
          <span className="font-mono text-xs text-ink-low shrink-0">{doc.agent_slug}</span>
        ) : null}
        {doc.content_version > 1 ? (
          <span className="text-xs text-ink-low tnum shrink-0" title={submissionTitle(doc)}>v{doc.content_version}</span>
        ) : null}
        <Age seconds={ageSeconds(doc.submitted_at, nowMs)} title={submissionTitle(doc)} />
        {queue === "snoozed" && doc.snoozed_until ? (
          <Pill value={`snoozed · ${formatReturnDate(snoozedUntilDate(doc.snoozed_until), nowMs)}`} tone="muted" />
        ) : (
          <Pill value={doc.status} tone={reviewStatusTone(doc.status)} />
        )}
      </Link>
      <ShortId id={doc.id} className="shrink-0" />
      {doc.status === "pending" ? (
        <>
          <SnoozeControl
            doc={doc}
            onPick={onSnooze}
            disabled={snoozing}
            open={menuOpen}
            onOpenChange={onMenuOpenChange}
          />
          <button type="button" className="btn btn-sm btn-second" disabled={approving} onClick={onApprove}>
            Approve
          </button>
        </>
      ) : null}
    </li>
  );
}
